/**
 * Options produits sur-mesure (remplace Globo Product Options).
 * Documentation : docs/product-options/README.md
 *
 * 1. Fiche produit : construit le formulaire à partir de la configuration JSON
 *    (snippets/product-options-config.liquid), affiche/masque les questions selon
 *    les réponses, et ajoute au panier l'article + ses suppléments en une requête.
 * 2. Panier : chaque supplément suit la quantité de son article ; il est retiré
 *    si l'article est supprimé.
 */
(function () {
  'use strict';

  var SUPPLEMENT_HANDLE = 'supplement-couture';
  var KEY_PROP = '_opt_key';
  var PARENT_PROP = '_opt_parent';

  // ---------------------------------------------------------------------------
  // Utilitaires
  // ---------------------------------------------------------------------------

  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (key) {
      var value = attrs[key];
      if (value === null || value === undefined || value === false) return;
      if (key === 'text') node.textContent = value;
      else if (key === 'html') node.innerHTML = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    (children || []).forEach(function (child) {
      if (child) node.appendChild(child);
    });
    return node;
  }

  var uid = 0;
  function nextId(prefix) {
    uid += 1;
    return prefix + '-' + uid;
  }

  var ICON_HELP =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="M6.3 6.2a1.8 1.8 0 1 1 2.4 1.7c-.5.2-.7.6-.7 1.1v.3M8 11.3v.01" stroke-linecap="round"/></svg>';
  var ICON_RULER =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="1.5" y="5" width="13" height="6" rx="1"/><path d="M4.5 5v2.5M7 5v1.5M9.5 5v2.5M12 5v1.5" stroke-linecap="round"/></svg>';
  var ICON_CHEVRON =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ---------------------------------------------------------------------------
  // Guide des mesures (dialog partagé, contenu dans snippets/measure-guide.liquid)
  // ---------------------------------------------------------------------------

  function openGuide(measureKey) {
    var dialog = document.querySelector('[data-measure-guide]');
    if (!dialog) return;
    var items = dialog.querySelectorAll('[data-measure]');
    items.forEach(function (item) {
      item.hidden = !!measureKey && item.getAttribute('data-measure') !== measureKey;
    });
    dialog.classList.toggle('is-single', !!measureKey);
    var all = dialog.querySelector('[data-measure-guide-all]');
    if (all) all.hidden = !measureKey;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    var body = dialog.querySelector('[data-measure-guide-body]');
    if (body) body.scrollTop = 0;
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-open-measure]');
    if (trigger) {
      e.preventDefault();
      openGuide(trigger.getAttribute('data-open-measure'));
      return;
    }
    var showAll = e.target.closest('[data-measure-guide-all]');
    if (showAll) {
      e.preventDefault();
      openGuide('');
      return;
    }
    var dialog = e.target.closest('[data-measure-guide]');
    if (dialog && (e.target === dialog || e.target.closest('[data-measure-guide-close]'))) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  });

  // ---------------------------------------------------------------------------
  // Formulaire d'options (fiche produit)
  // ---------------------------------------------------------------------------

  function ProductOptions(root) {
    this.root = root;
    this.formId = root.getAttribute('data-form-id');
    this.fieldsEl = root.querySelector('[data-product-options-fields]');
    this.summaryEl = root.querySelector('[data-product-options-summary]');
    this.config = JSON.parse(root.querySelector('[data-product-options-config]').textContent);
    this.texts = this.config.texts || {};
    this.pantalon = root.getAttribute('data-pantalon');
    this.fields = []; // { id, def, wrapper, inputs }
    this.build();
    this.refresh();
    root.addEventListener('change', this.refresh.bind(this));
    root.addEventListener('input', this.refresh.bind(this));
    root.productOptions = this;
  }

  ProductOptions.prototype.fieldIds = function () {
    var config = this.config;
    var ids = (config.profiles[this.root.getAttribute('data-profile')] || []).slice();
    if (this.pantalon && this.pantalon !== 'Aucun') ids = ids.concat(config.modules.pantalon || []);
    if (this.root.getAttribute('data-manches') === 'true') ids = ids.concat(config.modules.manches || []);
    return ids;
  };

  ProductOptions.prototype.supplementFor = function (name) {
    if (!name) return null;
    if (name === '@pantalon') name = this.pantalon;
    var supplement = this.config.supplements[name];
    if (!supplement) {
      console.warn('[product-options] Variante introuvable dans « Supplément couture » :', name);
      return null;
    }
    return { name: name, id: supplement.id, price: supplement.price };
  };

  ProductOptions.prototype.build = function () {
    var self = this;
    this.fieldIds().forEach(function (id) {
      var def = self.config.fields[id];
      if (!def) return;
      var node = def.type === 'group' ? self.buildGroup(id, def) : self.buildField(id, def);
      self.fieldsEl.appendChild(node);
    });
  };

  ProductOptions.prototype.register = function (id, def, wrapper, inputs) {
    this.fields.push({ id: id, def: def, wrapper: wrapper, inputs: inputs });
  };

  ProductOptions.prototype.inputName = function (def) {
    return 'properties[' + def.property + ']';
  };

  ProductOptions.prototype.buildLabel = function (def, forId) {
    var label = el(forId ? 'label' : 'span', { class: 'product-options__label', for: forId || null }, [
      el('span', { text: def.label }),
    ]);
    if (def.optional) {
      label.appendChild(el('span', { class: 'product-options__optional', text: '(' + (this.texts.optional || 'facultatif') + ')' }));
    }
    return label;
  };

  ProductOptions.prototype.buildField = function (id, def) {
    var self = this;
    var wrapper = el('div', { class: 'product-options__field product-options__field--' + def.type, 'data-po-field': id });
    var inputs = [];

    if (def.type === 'choice' || def.type === 'cards') {
      var fieldset = el('fieldset', { class: 'product-options__fieldset' });
      fieldset.appendChild(el('legend', { class: 'product-options__label' }, [el('span', { text: def.label })]));
      if (def.gate && self.texts.gate_hint) {
        fieldset.appendChild(el('p', { class: 'product-options__gate-hint', 'data-po-gate-hint': true, text: self.texts.gate_hint }));
      }
      var list = el('div', { class: def.type === 'cards' ? 'product-options__cards' : 'product-options__pills' });
      def.options.forEach(function (option, index) {
        var inputId = nextId('po-opt');
        var supplement = self.supplementFor(option.supplement);
        var input = el('input', {
          type: 'radio',
          id: inputId,
          name: self.inputName(def),
          value: option.value,
          form: self.formId,
          checked: !def.gate && index === 0,
          'data-supplement': supplement ? supplement.name : null,
        });
        inputs.push(input);
        var labelChildren = [el('span', { class: 'product-options__option-title', text: option.value })];
        if (supplement) {
          labelChildren.push(el('span', { class: 'product-options__price', text: '+' + formatMoney(supplement.price) }));
        } else if (option.free) {
          labelChildren.push(el('span', { class: 'product-options__price product-options__price--free', text: self.texts.free || 'Inclus' }));
        }
        if (option.hint && def.type === 'cards') {
          labelChildren.push(el('span', { class: 'product-options__hint', text: option.hint }));
        }
        list.appendChild(el('div', { class: 'product-options__choice' }, [input, el('label', { for: inputId }, labelChildren)]));
      });
      fieldset.appendChild(list);
      wrapper.appendChild(fieldset);
      if (def.note_if) {
        wrapper.appendChild(el('p', { class: 'product-options__note', 'data-po-note': true, hidden: true }));
      }
    } else if (def.type === 'select') {
      var selectId = nextId('po-select');
      wrapper.appendChild(this.buildLabel(def, selectId));
      var select = el('select', { id: selectId, name: this.inputName(def), form: this.formId, class: 'product-options__select' });
      select.appendChild(el('option', { value: '', text: 'Choisir' }));
      def.options.forEach(function (value) {
        select.appendChild(el('option', { value: value, text: value }));
      });
      inputs.push(select);
      wrapper.appendChild(select);
    } else {
      var inputIdText = nextId('po-input');
      var labelRow = el('div', { class: 'product-options__label-row' }, [this.buildLabel(def, inputIdText)]);
      if (def.guide) {
        labelRow.appendChild(
          el('button', {
            type: 'button',
            class: 'product-options__help',
            'data-open-measure': def.guide,
            'aria-label': 'Comment mesurer : ' + def.label,
            html: ICON_HELP,
          })
        );
      }
      wrapper.appendChild(labelRow);
      var control;
      if (def.type === 'textarea') {
        control = el('textarea', { id: inputIdText, rows: 3, maxlength: 500 });
      } else if (def.type === 'number') {
        control = el('input', { id: inputIdText, type: 'number', inputmode: 'decimal', min: 0, max: 300, step: 0.5 });
      } else {
        control = el('input', { id: inputIdText, type: 'text', maxlength: 200 });
      }
      control.setAttribute('name', this.inputName(def));
      control.setAttribute('form', this.formId);
      control.className = 'product-options__input';
      if (def.placeholder) control.setAttribute('placeholder', def.placeholder);
      inputs.push(control);
      if (def.unit) {
        wrapper.appendChild(el('div', { class: 'product-options__unit' }, [control, el('span', { text: def.unit })]));
      } else {
        wrapper.appendChild(control);
      }
    }

    if (def.help) wrapper.appendChild(el('p', { class: 'product-options__help-text', text: def.help }));
    this.register(id, def, wrapper, inputs);
    return wrapper;
  };

  ProductOptions.prototype.buildGroup = function (id, def) {
    var self = this;
    var details = el('details', { class: 'product-options__group', 'data-po-field': id, open: true });
    var summary = el('summary', { class: 'product-options__group-title' }, [
      el('span', { class: 'product-options__group-icon', html: ICON_RULER }),
      el('span', { text: def.label }),
      el('span', { class: 'product-options__optional', text: '(' + (this.texts.optional || 'facultatif') + ')' }),
      el('span', { class: 'product-options__chevron', html: ICON_CHEVRON }),
    ]);
    details.appendChild(summary);
    // Le bloc est enregistré avant ses mesures : refresh() évalue le parent en premier.
    this.register(id, def, details, []);
    var body = el('div', { class: 'product-options__group-body' });
    body.appendChild(el('p', { class: 'product-options__group-intro', text: this.texts.measures_later }));
    body.appendChild(
      el('button', {
        type: 'button',
        class: 'product-options__guide-link',
        'data-open-measure': '',
        html: ICON_RULER + '<span>' + (this.texts.guide_link || 'Comment prendre mes mesures ?') + '</span>',
      })
    );
    var grid = el('div', { class: 'product-options__measures' });
    def.fields.forEach(function (childId) {
      var childDef = self.config.fields[childId];
      if (childDef) grid.appendChild(self.buildField(childId, childDef));
    });
    body.appendChild(grid);
    details.appendChild(body);
    return details;
  };

  /** Valeur courante d'une question (null si masquée ou vide). */
  ProductOptions.prototype.valueOf = function (field) {
    if (field.hidden) return null;
    for (var i = 0; i < field.inputs.length; i++) {
      var input = field.inputs[i];
      if (input.type === 'radio') {
        if (input.checked) return input.value;
      } else if (input.value !== '') {
        return input.value;
      }
    }
    return null;
  };

  ProductOptions.prototype.findField = function (id) {
    for (var i = 0; i < this.fields.length; i++) {
      if (this.fields[i].id === id) return this.fields[i];
    }
    return null;
  };

  ProductOptions.prototype.isVisible = function (def) {
    if (!def.show_if) return true;
    var self = this;
    return Object.keys(def.show_if).every(function (otherId) {
      var other = self.findField(otherId);
      var value = other ? self.valueOf(other) : null;
      return value !== null && def.show_if[otherId].indexOf(value) !== -1;
    });
  };

  ProductOptions.prototype.groupOf = function (field) {
    var group = field.wrapper.parentNode && field.wrapper.parentNode.closest('[data-po-field]');
    return group ? this.findField(group.getAttribute('data-po-field')) : null;
  };

  ProductOptions.prototype.refresh = function () {
    var self = this;
    var blocked = false; // une question "gate" sans réponse masque toutes les suivantes
    // Ordre de la config : une question ne dépend que de questions placées avant elle.
    this.fields.forEach(function (field) {
      var group = self.groupOf(field);
      var visible = !blocked && self.isVisible(field.def) && !(group && group.hidden);
      field.hidden = !visible;
      field.wrapper.hidden = !visible;
      field.inputs.forEach(function (input) {
        input.disabled = !visible;
      });
      var empty = visible && self.valueOf(field) === null;
      if (field.def.gate) {
        if (empty) blocked = true;
        field.wrapper.classList.toggle('product-options__field--pending', empty);
        var hint = field.wrapper.querySelector('[data-po-gate-hint]');
        if (hint) hint.hidden = !empty;
      }
      if ((field.def.gate || field.def.required) && !empty) {
        field.wrapper.classList.remove('product-options__field--error');
      }
      if (field.def.note_if) {
        var note = field.wrapper.querySelector('[data-po-note]');
        var text = visible ? field.def.note_if[self.valueOf(field)] : null;
        note.hidden = !text;
        note.textContent = text || '';
      }
    });
    this.renderSummary();
  };

  /** Première question "gate" ou "required" visible restée sans réponse (null si tout est bon). */
  ProductOptions.prototype.missingRequired = function () {
    var self = this;
    for (var i = 0; i < this.fields.length; i++) {
      var field = this.fields[i];
      if ((field.def.gate || field.def.required) && !field.hidden && self.valueOf(field) === null) return field;
    }
    return null;
  };

  ProductOptions.prototype.selectedSupplements = function () {
    var self = this;
    var list = [];
    this.fields.forEach(function (field) {
      if (field.hidden) return;
      field.inputs.forEach(function (input) {
        if (input.type === 'radio' && input.checked && input.getAttribute('data-supplement')) {
          var supplement = self.supplementFor(input.getAttribute('data-supplement'));
          if (supplement) list.push(supplement);
        }
      });
    });
    return list;
  };

  ProductOptions.prototype.renderSummary = function () {
    var supplements = this.selectedSupplements();
    if (!this.summaryEl) return;
    if (!supplements.length) {
      this.summaryEl.hidden = true;
      this.summaryEl.innerHTML = '';
      return;
    }
    var total = supplements.reduce(function (sum, s) {
      return sum + s.price;
    }, 0);
    this.summaryEl.innerHTML = '';
    this.summaryEl.appendChild(
      el('p', { class: 'product-options__summary-title' }, [
        el('span', { text: this.texts.supplements_total || 'Suppléments sélectionnés' }),
        el('strong', { text: '+' + formatMoney(total) }),
      ])
    );
    this.summaryEl.appendChild(
      el('p', {
        class: 'product-options__summary-list',
        text: supplements
          .map(function (s) {
            return s.name + ' (' + formatMoney(s.price) + ')';
          })
          .join(' · '),
      })
    );
    this.summaryEl.hidden = false;
  };

  /** Propriétés supplémentaires calculées au moment de l'ajout. */
  ProductOptions.prototype.extraProperties = function () {
    var self = this;
    var props = {};
    var pendingMeasures = this.fields.some(function (field) {
      if (field.def.type !== 'group' || field.hidden) return false;
      return !self.fields.some(function (child) {
        return self.groupOf(child) === field && self.valueOf(child) !== null;
      });
    });
    if (pendingMeasures) {
      props[this.texts.measures_later_property || 'Mesures'] = this.texts.measures_later_value || 'À transmettre après la commande';
    }
    return props;
  };

  // ---------------------------------------------------------------------------
  // Ajout au panier : article + suppléments en une seule requête
  // ---------------------------------------------------------------------------

  function variantTitle(form, variantId) {
    var json = form.querySelector('[data-product-json]');
    if (!json) return '';
    try {
      var product = JSON.parse(json.textContent);
      var variant = product.variants.filter(function (v) {
        return String(v.id) === String(variantId);
      })[0];
      return variant && variant.title !== 'Default Title' ? variant.title : '';
    } catch (e) {
      return '';
    }
  }

  function showError(button, message) {
    var wrapper = button.closest('[data-add-action-wrapper]');
    var errors = wrapper && wrapper.querySelector('[data-add-action-errors]');
    button.classList.remove('loading');
    button.removeAttribute('disabled');
    if (errors) {
      errors.innerHTML = '<div class="errors">' + message + '</div>';
      errors.style.display = 'block';
      setTimeout(function () {
        errors.style.display = 'none';
      }, 6000);
    } else {
      window.alert(message);
    }
  }

  function afterAdd(button) {
    button.classList.remove('loading');
    button.classList.add('has-success');
    setTimeout(function () {
      button.classList.remove('has-success');
      button.removeAttribute('disabled');
    }, 3500);

    var routes = (window.theme && window.theme.routes) || {};
    fetch((routes.cart || '/cart') + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
      })
      .catch(function () {});

    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (document.querySelector('[data-ajax-disable]')) {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    } else if (drawer) {
      drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { detail: { reinit: true }, bubbles: true }));
    } else {
      window.location.href = routes.cart || '/cart';
    }
  }

  function addWithOptions(button, form, options) {
    var missing = options.missingRequired();
    if (missing) {
      missing.wrapper.classList.add('product-options__field--error');
      missing.wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      showError(button, (options.texts.gate_required || 'Merci de choisir : ') + missing.def.label);
      return;
    }
    var data = new FormData(form);
    var variantId = data.get('id');
    var quantity = parseInt(data.get('quantity'), 10) || 1;
    var properties = {};
    data.forEach(function (value, name) {
      var match = /^properties\[(.+)\]$/.exec(name);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value).trim();
    });
    var extra = options.extraProperties();
    Object.keys(extra).forEach(function (k) {
      properties[k] = extra[k];
    });

    var key = 'po' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    properties[KEY_PROP] = key;

    var main = { id: Number(variantId), quantity: quantity, properties: properties };
    if (data.get('selling_plan')) main.selling_plan = data.get('selling_plan');

    var forLabel = options.root.getAttribute('data-product-title');
    var vTitle = variantTitle(form, variantId);
    if (vTitle) forLabel += ' — ' + vTitle;

    // Les suppléments d'abord : Shopify affiche les derniers articles ajoutés en haut,
    // l'article principal apparaît ainsi au-dessus de ses suppléments.
    var items = options.selectedSupplements().map(function (s) {
      var props = {};
      props[options.texts.for_item || 'Pour'] = forLabel;
      props[PARENT_PROP] = key;
      return { id: s.id, quantity: quantity, properties: props };
    });
    items.push(main);

    button.setAttribute('disabled', true);
    button.classList.add('loading');

    var routes = (window.theme && window.theme.routes) || {};
    fetch((routes.cart || '/cart') + '/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok) throw json;
          return json;
        });
      })
      .then(function () {
        afterAdd(button);
      })
      .catch(function (error) {
        var message = (error && (error.description || error.message)) || 'Erreur réseau, merci de réessayer.';
        if (typeof message === 'object') message = JSON.stringify(message);
        showError(button, message);
      });
  }

  function optionsForForm(form) {
    // getAttribute : form.id renverrait le champ <input name="id"> du formulaire produit
    var formId = form && form.getAttribute && form.getAttribute('id');
    if (!formId) return null;
    var root = document.querySelector('[data-product-options][data-form-id="' + formId + '"]');
    return root && root.productOptions ? root.productOptions : null;
  }

  // Phase de capture sur window : passe avant le gestionnaire d'ajout du thème.
  window.addEventListener(
    'click',
    function (e) {
      var button = e.target.closest && e.target.closest('[data-add-to-cart]');
      if (!button || button.hasAttribute('data-add-to-cart-variant')) return;
      var form = button.closest('form');
      var options = optionsForForm(form);
      if (!options) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (button.classList.contains('loading')) return;
      addWithOptions(button, form, options);
    },
    true
  );

  window.addEventListener(
    'submit',
    function (e) {
      var options = optionsForForm(e.target);
      if (!options) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var button = e.target.querySelector('[data-add-to-cart]');
      if (button && !button.disabled) addWithOptions(button, e.target, options);
    },
    true
  );

  // ---------------------------------------------------------------------------
  // Panier : synchronisation des suppléments avec leur article
  // ---------------------------------------------------------------------------

  var syncing = false;

  function syncCart(cart) {
    if (syncing || !cart || !cart.items) return;
    var mains = {};
    cart.items.forEach(function (item) {
      var key = item.properties && item.properties[KEY_PROP];
      if (key) mains[key] = (mains[key] || 0) + item.quantity;
    });
    var updates = {};
    var needed = false;
    cart.items.forEach(function (item) {
      var parent = item.properties && item.properties[PARENT_PROP];
      var isSupplement = parent || item.handle === SUPPLEMENT_HANDLE;
      if (!isSupplement) return;
      var wanted = parent ? mains[parent] || 0 : 0;
      if (item.quantity !== wanted) {
        updates[item.key] = wanted;
        needed = true;
      }
    });
    if (!needed) return;

    syncing = true;
    var routes = (window.theme && window.theme.routes) || {};
    fetch((routes.cart || '/cart') + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (fresh) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: fresh }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      })
      .catch(function (err) {
        console.error('[product-options] Synchronisation des suppléments impossible', err);
      })
      .then(function () {
        setTimeout(function () {
          syncing = false;
        }, 500);
      });
  }

  document.addEventListener('theme:cart:change', function (e) {
    syncCart(e.detail && e.detail.cart);
  });

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  function init() {
    document.querySelectorAll('[data-product-options]').forEach(function (root) {
      if (root.productOptions) return;
      try {
        new ProductOptions(root);
      } catch (err) {
        console.error('[product-options] Configuration invalide', err);
      }
    });

    // Panier déjà rendu côté serveur (page panier ou drawer pré-rendu)
    if (document.querySelector('[data-opt-supplement]')) {
      var routes = (window.theme && window.theme.routes) || {};
      fetch((routes.cart || '/cart') + '.js')
        .then(function (r) {
          return r.json();
        })
        .then(syncCart)
        .catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
